import React, { useState, useMemo, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Checkbox } from "@shared/ui/checkbox";
import {
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Loader2,
} from "lucide-react";
import {
  useConsumerSearch,
  useMeterSearch,
  useAreaConfig,
} from "../../../hooks";
import { Consumer } from "../../../types";
import { transformMeterData } from "@features/wx/dispatcher/utils";
import { useSearchParams } from "react-router-dom";

// Define entity filters type
interface EntityFilters {
  page: number;
  limit: number;
  search_data?: string;
  area?: string;
}

interface EntitySelectionSectionProps {
  serviceAssociation: string;
  selectedEntities: any[];
  onEntitiesChange: (entities: any[]) => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "Active":
    case "Assigned":
    case "Operational":
    case "Current":
    case "Installed":
    case "ACTIVE":
      return "bg-green-100 text-green-800 border-0";
    case "Inactive":
    case "Unassigned":
    case "Under Maintenance":
    case "Overdue":
    case "Removed":
    case "INACTIVE":
      return "bg-orange-100 text-orange-800 border-0";
    case "Out of Service":
      return "bg-red-100 text-red-800 border-0";
    case "Pending":
      return "bg-blue-100 text-blue-800 border-0";
    default:
      return "bg-gray-100 text-gray-800 border-0";
  }
};

// Transform Consumer API data to match the UI format
const transformConsumerData = (consumers: Consumer[]) => {
  return consumers.map((consumer) => ({
    id: consumer.id.toString(),
    accountNo: consumer.consumerNo,
    name:
      consumer.consumerName ||
      `${consumer.firstName} ${consumer.lastName}`.trim(),
    phoneNo: consumer.contactNumber,
    category: "NA",
    area: consumer.areaCode || "N/A",
    subArea: consumer.premiseCode || "N/A",
    premise: consumer.premiseCode || "N/A",
    status: consumer.statusDisplay,
    originalData: consumer,
  }));
};

export const EntitySelectionSection: React.FC<EntitySelectionSectionProps> = ({
  serviceAssociation,
  selectedEntities,
  onEntitiesChange,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAreaDropdown, setShowAreaDropdown] = useState(false);
  const prevServiceAssociation = useRef(serviceAssociation);
  const itemsPerPage = 5;

  // ✅ FIXED - Get areas from dedicated API
  const { data: areaOptions } = useAreaConfig();

  // Parse filters from URL params - following UserTable pattern
  const getFiltersFromUrl = (): EntityFilters => {
    const page = parseInt(searchParams.get("page") || "1");

    const urlFilters: EntityFilters = {
      page,
      limit: itemsPerPage,
    };

    const queryParam = searchParams.get("query");
    if (queryParam) {
      urlFilters.search_data = queryParam;
    }

    const areaParam = searchParams.get("area");
    if (areaParam && areaParam !== "all") {
      urlFilters.area = areaParam;
    }

    return urlFilters;
  };

  const [filters, setFilters] = useState<EntityFilters>(getFiltersFromUrl);

  // Update filters when URL changes
  useEffect(() => {
    const newFilters = getFiltersFromUrl();

    setFilters(newFilters);
  }, [searchParams, serviceAssociation]);

  // Reset page when service association changes
  useEffect(() => {
    if (prevServiceAssociation.current !== serviceAssociation) {
      console.log(
        "Service association changed from",
        prevServiceAssociation.current,
        "to",
        serviceAssociation
      );
      const params = new URLSearchParams();
      params.set("page", "1");
      setSearchParams(params);
      prevServiceAssociation.current = serviceAssociation;
    }
  }, [serviceAssociation, setSearchParams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".area-filter-dropdown")) {
        setShowAreaDropdown(false);
      }
    };

    if (showAreaDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showAreaDropdown]);

  // ✅ FIXED - API calls with proper filter parameters
  const {
    data: consumerApiResponse,
    isLoading: isLoadingConsumers,
    error: consumerError,
  } = useConsumerSearch(
    {
      searchData: filters.search_data || "",
      limit: filters.limit,
      page: filters.page,
      area: filters.area,
    },
    {
      enabled: serviceAssociation === "Consumer",
    }
  );

  const {
    data: meterApiResponse,
    isLoading: isLoadingMeters,
    error: meterError,
  } = useMeterSearch(
    {
      searchData: filters.search_data || "",
      limit: filters.limit,
      page: filters.page,
      area: filters.area,
    },
    {
      enabled: serviceAssociation === "Meter",
    }
  );

  // ✅ FIXED - Get available areas from dedicated API instead of limited response
  const availableAreas = useMemo(() => {
    if (!areaOptions) return [];
    return areaOptions.map((area) => area.name).sort();
  }, [areaOptions]);

  // ✅ FIXED - Get entity data with proper pagination
  const getEntityData = () => {
    switch (serviceAssociation) {
      case "Meter":
        let transformedMeters = meterApiResponse?.results
          ? transformMeterData(meterApiResponse.results)
          : [];

        // ✅ Apply client-side area filtering if API doesn't support it
        if (filters.area && filters.area !== "all") {
          transformedMeters = transformedMeters.filter((meter) => {
            const meterArea = meter.area;
            return meterArea === filters.area;
          });
        }

        return {
          entities: transformedMeters,
          isLoading: isLoadingMeters,
          error: meterError,
          totalCount: meterApiResponse?.count || 0,
        };

      case "Consumer":
        let transformedConsumers = consumerApiResponse?.results
          ? transformConsumerData(consumerApiResponse.results)
          : [];

        // ✅ Apply client-side area filtering if API doesn't support it
        if (filters.area && filters.area !== "all") {
          transformedConsumers = transformedConsumers.filter((consumer) => {
            const consumerArea = consumer.area;
            return consumerArea === filters.area;
          });
        }

        return {
          entities: transformedConsumers,
          isLoading: isLoadingConsumers,
          error: consumerError,
          totalCount: consumerApiResponse?.count || 0,
        };

      default:
        return { entities: [], isLoading: false, error: null, totalCount: 0 };
    }
  };

  const { entities, isLoading, error, totalCount } = getEntityData();

  // ✅ FIXED - Calculate pagination info from API response
  const pagination = useMemo(() => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const currentPage = filters.page;
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalCount);

    return {
      currentPage,
      totalPages,
      totalItems: totalCount,
      itemsPerPage,
      startItem,
      endItem,
    };
  }, [totalCount, filters.page, itemsPerPage]);

  // ✅ NEW - Smart selection logic for current page
  const currentPageSelectionInfo = useMemo(() => {
    const currentPageEntityIds = entities.map((e) => e.id);
    const selectedOnCurrentPage = selectedEntities.filter((entity) =>
      currentPageEntityIds.includes(entity.id)
    );

    const isAllCurrentPageSelected =
      entities.length > 0 && selectedOnCurrentPage.length === entities.length;
    const isSomeCurrentPageSelected =
      selectedOnCurrentPage.length > 0 &&
      selectedOnCurrentPage.length < entities.length;

    return {
      selectedOnCurrentPage,
      isAllCurrentPageSelected,
      isSomeCurrentPageSelected,
      currentPageEntityIds,
    };
  }, [entities, selectedEntities]);

  // Handle search
  const handleSearch = (searchTerm: string, paramName: string = "query") => {
    const params = new URLSearchParams(searchParams);

    if (searchTerm.trim()) {
      params.set(paramName, searchTerm);
    } else {
      params.delete(paramName);
    }

    params.set("page", "1");
    setSearchParams(params);
  };

  // ✅ FIXED - Handle pagination properly
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  // Handle area filter
  const handleAreaFilter = (area: string) => {
    const params = new URLSearchParams(searchParams);
    if (area !== "all") {
      params.set("area", area);
    } else {
      params.delete("area");
    }
    params.set("page", "1");
    setSearchParams(params);
    setShowAreaDropdown(false);
  };

  const getSearchPlaceholder = () => {
    switch (serviceAssociation) {
      case "Meter":
        return "Search by meter number, type, or premise...";
      case "Consumer":
        return "Search by name, account number, or phone...";
      case "Asset":
        return "Search by asset name, ID, or type...";
      default:
        return "Search entities...";
    }
  };

  const handleEntitySelect = (entity: any, checked: boolean) => {
    if (checked) {
      const newEntities = [...selectedEntities, entity];

      onEntitiesChange(newEntities);
    } else {
      const newEntities = selectedEntities.filter((e) => e.id !== entity.id);

      onEntitiesChange(newEntities);
    }
  };

  // ✅ NEW - Smart Select All for current page only
  const handleSelectAllCurrentPage = () => {
    const { currentPageEntityIds, isAllCurrentPageSelected } =
      currentPageSelectionInfo;

    if (isAllCurrentPageSelected) {
      // Remove current page entities from selection
      const newSelection = selectedEntities.filter(
        (entity) => !currentPageEntityIds.includes(entity.id)
      );

      onEntitiesChange(newSelection);
    } else {
      // Add current page entities to selection (avoiding duplicates)
      const currentPageEntities = entities.filter(
        (entity) =>
          !selectedEntities.some((selected) => selected.id === entity.id)
      );
      const newSelection = [...selectedEntities, ...currentPageEntities];

      onEntitiesChange(newSelection);
    }
  };

  // ✅ NEW - Clear all selections globally
  const handleClearAllSelections = () => {
    onEntitiesChange([]);
  };

  const removeSelectedEntity = (entityId: string) => {
    onEntitiesChange(selectedEntities.filter((e) => e.id !== entityId));
  };

  const isEntitySelected = (entity: any) => {
    return selectedEntities.some((e) => e.id === entity.id);
  };

  const getEntityDisplayName = (entity: any) => {
    if (serviceAssociation === "Meter") {
      return entity.meterNo;
    } else if (serviceAssociation === "Consumer") {
      return entity.name;
    }
    return entity.id;
  };

  // ✅ NEW - Updated selection states
  const { isAllCurrentPageSelected, isSomeCurrentPageSelected } =
    currentPageSelectionInfo;

  return (
    <Card className="bg-white border border-gray-200 rounded-lg shadow-sm max-h-[700px] flex flex-col">
      <CardHeader className="pb-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-gray-900">
            Select {serviceAssociation}
          </CardTitle>

          {/* ✅ NEW - Smart button group */}
          <div className="flex items-center gap-2">
            {/* Select All / Unselect All for current page */}
            <Button
              onClick={handleSelectAllCurrentPage}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              disabled={isLoading || entities.length === 0}
            >
              <Checkbox
                checked={isAllCurrentPageSelected}
                ref={(el) => {
                  if (el && el.querySelector('[role="checkbox"]')) {
                    const checkbox = el.querySelector(
                      '[role="checkbox"]'
                    ) as HTMLElement;
                    if (checkbox) {
                      (checkbox as any).indeterminate =
                        isSomeCurrentPageSelected;
                    }
                  }
                }}
              />
              {isAllCurrentPageSelected ? "Unselect All" : "Select All"}
            </Button>

            {/* Clear All button - only show when there are selections */}
            {selectedEntities.length > 0 && (
              <Button
                onClick={handleClearAllSelections}
                variant="outline"
                size="sm"
                className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              >
                <X className="h-3 w-3" />
                Clear All ({selectedEntities.length})
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 flex-1 overflow-hidden flex flex-col">
        {/* Search and Filter */}
        <div className="flex gap-3 flex-shrink-0">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder={getSearchPlaceholder()}
              value={filters.search_data || ""}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-9 border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              disabled={isLoading}
            />
          </div>

          {/* Area Filter Dropdown */}
          <div className="relative area-filter-dropdown">
            <Button
              variant="outline"
              className="flex items-center gap-2 border-gray-300 text-gray-700 hover:bg-gray-50 min-w-[120px] justify-between"
              disabled={isLoading}
              onClick={() => setShowAreaDropdown(!showAreaDropdown)}
            >
              <span className="truncate">{filters.area || "All Areas"}</span>
              <ChevronDown className="h-4 w-4 flex-shrink-0" />
            </Button>

            {showAreaDropdown && (
              <div className="absolute top-full left-0 mt-1 w-full min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                <div className="p-1">
                  <button
                    onClick={() => handleAreaFilter("all")}
                    className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-100 ${
                      !filters.area || filters.area === "all"
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-gray-700"
                    }`}
                  >
                    All Areas
                  </button>
                  {availableAreas.map((area) => (
                    <button
                      key={area}
                      onClick={() => handleAreaFilter(area)}
                      className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-100 ${
                        filters.area === area
                          ? "bg-blue-50 text-blue-700 font-medium"
                          : "text-gray-700"
                      }`}
                    >
                      {area}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Selected Entities Summary */}
        {selectedEntities.length > 0 && (
          <div className="flex flex-wrap gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200 max-h-24 overflow-y-auto flex-shrink-0">
            {selectedEntities.map((entity) => (
              <Badge
                key={entity.id}
                className="bg-blue-100 text-blue-800 border-0 flex items-center gap-1"
              >
                <span className="truncate max-w-32">
                  {getEntityDisplayName(entity)}
                </span>
                <X
                  className="h-3 w-3 cursor-pointer hover:text-blue-600"
                  onClick={() => removeSelectedEntity(entity.id)}
                />
              </Badge>
            ))}
          </div>
        )}

        {/* Entity List Header */}
        <div className="flex items-center justify-between text-sm text-gray-600 border-b pb-2 flex-shrink-0">
          <span>
            {(serviceAssociation === "Consumer" ||
              serviceAssociation === "Meter") && (
              <span className="text-blue-600 ml-1">(Total: {totalCount})</span>
            )}
          </span>
          <span>
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            <span className="ml-2 text-gray-600">
              Loading {serviceAssociation}...
            </span>
          </div>
        ) : (
          <>
            {/* Entity List */}
            <div className="space-y-2 flex-1 overflow-y-auto min-h-0">
              {entities.map((entity) => (
                <div
                  key={entity.id}
                  className={`p-4 border rounded-lg transition-all hover:shadow-sm ${
                    isEntitySelected(entity)
                      ? "border-l-4 border-l-blue-500 bg-blue-50 border-blue-200"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={isEntitySelected(entity)}
                      onCheckedChange={(checked) =>
                        handleEntitySelect(entity, checked as boolean)
                      }
                      className="mt-1"
                    />

                    <div className="flex-1 min-w-0">
                      {/* Meter Rendering */}
                      {serviceAssociation === "Meter" && (
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500 text-xs">
                              Meter Number
                            </p>
                            <span className="font-medium text-blue-600 block truncate">
                              {(entity as any).meterNo}
                            </span>
                            <p className="text-gray-500 text-xs mt-1">
                              Meter Type
                            </p>
                            <p className="text-gray-700 truncate">
                              {(entity as any).meterTypeDisplay}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-xs">
                              Device Number
                            </p>
                            <span className="text-gray-700 block truncate">
                              {(entity as any).deviceNo}
                            </span>
                            <p className="text-gray-500 text-xs mt-1">
                              Product Code
                            </p>
                            <p className="text-gray-700 truncate">
                              {(entity as any).productCode}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-xs">
                              Assignment Status
                            </p>
                            <Badge
                              className={getStatusColor(
                                (entity as any).assignmentStatus
                              )}
                            >
                              {(entity as any).assignmentStatusDisplay}
                            </Badge>
                            <p className="text-gray-500 text-xs mt-1">
                              Physical Status
                            </p>
                            <Badge
                              className={getStatusColor(
                                (entity as any).physicalStatus
                              )}
                            >
                              {(entity as any).physicalStatusDisplay}
                            </Badge>
                          </div>
                          <div>
                            <p className="text-gray-500 text-xs">
                              Current Reading
                            </p>
                            <p className="text-gray-600">
                              {(entity as any).currentReading
                                ? `${(entity as any).currentReading}`
                                : "N/A"}
                            </p>
                            <p className="text-gray-500 text-xs mt-1">Area</p>
                            <p className="text-gray-600 truncate">
                              {(entity as any).area}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Consumer Rendering */}
                      {serviceAssociation === "Consumer" &&
                        (() => {
                          const consumerEntity = entity as any;
                          return (
                            <div className="grid grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-gray-500 text-xs">
                                  Account Number
                                </p>
                                <span className="font-medium text-blue-600 block truncate">
                                  {consumerEntity.accountNo}
                                </span>
                                <p className="text-gray-500 text-xs mt-1">
                                  Consumer Name
                                </p>
                                <p className="text-gray-700 truncate">
                                  {consumerEntity.name}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500 text-xs">
                                  Phone Number
                                </p>
                                <span className="text-gray-700 block truncate">
                                  {consumerEntity.phoneNo}
                                </span>
                                <p className="text-gray-500 text-xs mt-1">
                                  Email
                                </p>
                                <p className="text-gray-700 truncate">
                                  {consumerEntity.originalData?.email || "N/A"}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500 text-xs">Status</p>
                                <Badge
                                  className={getStatusColor(
                                    consumerEntity.status
                                  )}
                                >
                                  {consumerEntity.status}
                                </Badge>
                                <p className="text-gray-500 text-xs mt-1">
                                  Premise
                                </p>
                                <p className="text-gray-700 truncate">
                                  {consumerEntity.premise}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500 text-xs">Area</p>
                                <p className="text-gray-600 truncate">
                                  {consumerEntity.area}
                                </p>
                                <p className="text-gray-500 text-xs mt-1">ID</p>
                                <p className="text-gray-600">
                                  {consumerEntity.originalData?.id}
                                </p>
                              </div>
                            </div>
                          );
                        })()}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ✅ FIXED - Pagination with proper page numbers */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-4 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handlePageChange(Math.max(1, pagination.currentPage - 1))
                  }
                  disabled={pagination.currentPage === 1}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                {/* Show page numbers dynamically */}
                {Array.from(
                  { length: Math.min(5, pagination.totalPages) },
                  (_, i) => {
                    // Calculate which pages to show
                    const startPage = Math.max(1, pagination.currentPage - 2);
                    const endPage = Math.min(
                      pagination.totalPages,
                      startPage + 4
                    );
                    const adjustedStartPage = Math.max(1, endPage - 4);

                    const page = adjustedStartPage + i;
                    if (page > pagination.totalPages) return null;

                    return (
                      <Button
                        key={page}
                        variant={
                          pagination.currentPage === page
                            ? "default"
                            : "outline"
                        }
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className={`w-8 h-8 p-0 ${
                          pagination.currentPage === page
                            ? "bg-blue-600 text-white"
                            : "border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {page}
                      </Button>
                    );
                  }
                )}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handlePageChange(
                      Math.min(
                        pagination.totalPages,
                        pagination.currentPage + 1
                      )
                    )
                  }
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
